import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProcessOrigin, SignoffTerm } from '@testflow/shared';
import { can } from '@testflow/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { IdLabel, ResultChip, StateChip } from '../../components/atoms.js';
import { ConfirmDialog } from '../../components/confirm-dialog.js';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { pickLang } from '../../lib/pick-lang.js';
import { sortActivePlans } from '../../lib/plan-selection.js';
import { plansApi } from '../../lib/plans-api.js';
import { projectConfigApi } from '../../lib/project-config-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { scopeHintFrom } from '../../lib/scope-hint.js';
import type { SignoffListItem } from '../../lib/signoff-api.js';
import {
  isConfirmed,
  isEvidenceDrifted,
  isOrphaned,
  isVisibleSignoffCard,
  needsSignoffNote,
  signoffApi,
} from '../../lib/signoff-api.js';
import { useCurrentProject } from '../../lib/use-project.js';

// #78-Regel (Task 6, Issue #83): eine stille Kappung liest sich wie Vollständigkeit — bei vielen
// Prozessen (echter Befund: 34 am Import-Knoten "Order to Cash") wird die aufgeklappte Liste
// begrenzt UND die Begrenzung angeschrieben, statt einfach nur abzuschneiden.
const PROCESS_ORIGIN_LIMIT = 20;

// Design-Spec §3.10 (Sign-off-Seite, SGN-1): eine Karte je Struktur-Knoten (Teilprojekt/Stream)
// mit Bestätiger + Datum + automatisch verlinktem Nachweis (bestandene Läufe). Task F4 schließt den
// Begriffs-Loop: `project_settings.signoff_term` (admin/panels/signoff-term-panel.tsx) bestimmt, ob
// der Button/Status "Bestätigen"/"Bestätigt" oder "Abnahme" heißt (signoff.bestaetigung/
// signoff.abnahme — dieselben Keys wie im Admin-Panel, damit der Begriff überall konsistent ist).
// Solange die Einstellung noch lädt oder der Begriff `bestaetigung` ist, bleibt der feste Text
// `signoff.confirm`/`signoff.confirmed` unverändert (kein zusätzlicher Key nötig).
// `isConfirmed` liegt in lib/signoff-api.ts, weil der Abnahmestand im Testplan-Detail dieselbe
// Regel braucht — zweimal ausgeschrieben würde sie beim nächsten Umbau auseinanderdriften.

function confirmLabel(t: (key: string) => string, signoffTerm: SignoffTerm | undefined): string {
  return signoffTerm === 'abnahme' ? t('signoff.abnahme') : t('signoff.confirm');
}

function confirmedStatusLabel(
  t: (key: string) => string,
  signoffTerm: SignoffTerm | undefined,
): string {
  return signoffTerm === 'abnahme' ? t('signoff.abnahme') : t('signoff.confirmed');
}

// #83 (Task 6, Issue #83 "woraus speist sich das?"): die Prozess-Herkunft einer Karte — Kette
// Testfall → Prozess → process_labels → Knoten. Kollabiert per Default (state-basiert statt
// natives <details>, damit sich der Auf-/Zuklapp-Zustand testen lässt statt sich auf
// UA-Stylesheet-Verhalten zu verlassen, das jsdom nicht abbildet).
function ProcessOriginDisclosure({ origin }: { origin: ProcessOrigin }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  // #112 (Gesamtreview-Befund): An einem Teilprojekt ist die Herkunft die Prozessliste, an einem
  // STREAM sind es die Ketten (`e2e_processes`) — `processes` ist dort per Definition leer. Die
  // Abbruchbedingung stand vorher nur auf `processes`, also blieb die Herkunftszeile an jedem
  // Stream leer und `e2e_processes` wurde nirgends gelesen: ein Feld ohne Konsequenz, und die
  // Frage aus #83 ("woraus speist sich diese Abnahme?") an Streams wieder unbeantwortet.
  if (origin.processes.length === 0 && origin.e2e_processes.length === 0) return null;

  // Eine gemeinsame Liste, damit Kappung UND Zaehl-Beschriftung dieselbe Menge meinen. Die Kette
  // traegt zusaetzlich ihre human_id (ETO-E2E-001) — aehnlich benannte Ketten sind sonst nicht
  // auseinanderzuhalten.
  const entries = [
    ...origin.processes.map((p) => ({
      key: `process:${p.process_id}`,
      humanId: null as string | null,
      name: pickLang(i18n.resolvedLanguage, p.name_de, p.name_en),
    })),
    ...origin.e2e_processes.map((c) => ({
      key: `e2e:${c.e2e_id}`,
      humanId: c.human_id,
      name: pickLang(i18n.resolvedLanguage, c.name_de, c.name_en),
    })),
  ];
  const shown = entries.slice(0, PROCESS_ORIGIN_LIMIT);

  // Die Beschriftung benennt, WAS gezaehlt wird. Der dritte Fall (beides gefuellt) kann laut
  // ProcessOrigin nicht auftreten — er steht trotzdem hier, weil "N Prozesse" ueber einer Liste,
  // in der Ketten stehen, eine Falschaussage waere und kein Rundungsfehler.
  const countLabel =
    origin.e2e_processes.length === 0
      ? t('signoff.processCount', { count: entries.length })
      : origin.processes.length === 0
        ? t('signoff.e2eCount', { count: entries.length })
        : t('signoff.originCount', { count: entries.length });

  return (
    <div className="text-xs">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="text-text-muted underline decoration-dotted underline-offset-2 hover:text-text-body"
      >
        {countLabel}
      </button>
      {open ? (
        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-text-body">
          {shown.map((e) => (
            <li key={e.key}>
              {e.humanId ? (
                <span className="mr-1">
                  <IdLabel id={e.humanId} />
                </span>
              ) : null}
              {e.name}
            </li>
          ))}
        </ul>
      ) : null}
      {open && entries.length > shown.length ? (
        <div className="mt-1 text-text-muted">
          {t('signoff.processOriginLimited', {
            shown: shown.length,
            total: entries.length,
          })}
        </div>
      ) : null}
    </div>
  );
}

// #83: die ausgeblendeten Knoten (kein Testfall im Scope dieses Plans, kein aktiver Sign-off) —
// Nutzerentscheidung: keine stille Kappung. Kollabiert per Default, dieselbe state-basierte Technik
// wie oben. Kein eigener Transport-Typ mehr (Whole-Branch-Review): der Server liefert seit dem
// Umbau von #83 nur noch die volle SignoffListItem-Liste, node_id/node_name reichen hier.
function EmptyNodesDisclosure({
  nodes,
}: { nodes: Pick<SignoffListItem, 'node_id' | 'node_name'>[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (nodes.length === 0) return null;

  return (
    <div className="rounded-card border border-[var(--border)] bg-bg-surface p-3 text-sm">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="font-medium text-text-body"
      >
        {t('signoff.emptyNodesCount', { count: nodes.length })}
      </button>
      {open ? (
        <ul className="mt-2 list-disc space-y-0.5 pl-4 text-text-muted">
          {nodes.map((n) => (
            <li key={n.node_id}>{n.node_name}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// #88: Textfeld für Kommentar bzw. Widerrufsbegründung im Bestätigungsdialog. 2000 Zeichen = die
// Obergrenze der API (note / reason), damit der Server nie wegen der Länge ablehnt.
function DraftField({
  id,
  label,
  hint,
  value,
  onChange,
}: {
  id: string;
  label: string;
  hint: string | null;
  value: string;
  onChange: (v: string) => void;
}) {
  // #88 (Final-Review F8): der Hinweis erklärt, warum der Dialog-Knopf gesperrt ist — per
  // aria-describedby am Textfeld, damit ihn auch ein Screenreader beim Fokus vorliest.
  const hintId = `${id}-hint`;
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-text-body">
        {label}
      </label>
      <textarea
        id={id}
        rows={3}
        maxLength={2000}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={hint ? hintId : undefined}
        className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-body"
      />
      {hint ? (
        <p id={hintId} className="text-xs text-status-warn">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function SignoffCard({
  item,
  canConfirm,
  pending,
  signoffTerm,
  onConfirm,
  onRevoke,
}: {
  item: SignoffListItem;
  canConfirm: boolean;
  pending: boolean;
  signoffTerm: SignoffTerm | undefined;
  onConfirm: () => void;
  onRevoke: () => void;
}) {
  const { t } = useTranslation();
  const confirmed = isConfirmed(item);
  // Blocker (Whole-Branch-Review): eine bestätigte Abnahme, deren Testfall danach aus dem
  // Plan-Scope genommen wurde — die Bestätigung ist noch aktiv, aber ohne Grundlage. Muss
  // hervorgehoben werden, nicht versteckt (die alte serverseitige Fassung blendete genau diesen
  // Fall unwiderruflich aus: Karte weg, Widerrufen-Knopf unerreichbar, DB weiter "abgenommen").
  const orphaned = isOrphaned(item);
  // #88: die Grundlage hat sich seit der Freigabe geändert (Server-Flag) — gleiche Hervorhebung wie
  // bei orphaned, aber eigener Hinweis. Beide können zugleich zutreffen (Grundlage ganz weg UND
  // Zahlen anders) und werden dann beide gezeigt.
  const drifted = isEvidenceDrifted(item);
  const revocation = item.last_revocation;

  return (
    <div className="flex flex-col gap-2 rounded-card border border-[var(--border)] bg-bg-surface p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="font-bold text-text-primary">{item.node_name}</span>
        <StateChip
          label={confirmed ? confirmedStatusLabel(t, signoffTerm) : t('signoff.open')}
          // Orphaned bleibt fachlich "bestätigt" (revoked_at ist null), bekommt aber den Warn-Ton —
          // eine Abnahme ohne Grundlage braucht Aufmerksamkeit, auch wenn ihr Status formal steht.
          // #88: gilt ebenso bei geänderter Grundlage (drifted).
          tone={confirmed && !orphaned && !drifted ? 'ok' : 'warn'}
        />
      </div>

      {confirmed && item.confirmer_name && item.confirmed_at ? (
        <span className="text-sm text-text-muted">
          {t('signoff.confirmedBy', {
            name: item.confirmer_name,
            date: item.confirmed_at.slice(0, 10),
          })}
        </span>
      ) : null}

      {/* #88: der Kommentar der Freigabe gehört zur Abnahme und steht deshalb auf der Karte —
          sonst wäre die Pflichtbegründung unter 100 % ein Feld, das nie wieder jemand sieht.
          pre-line erhält die Zeilenumbrüche aus dem Textfeld. */}
      {confirmed && item.note ? (
        <span className="whitespace-pre-line text-sm text-text-body">
          {t('signoff.noteShown', { note: item.note })}
        </span>
      ) : null}

      {orphaned ? (
        <span className="rounded-btn bg-status-warn/10 px-2 py-1 text-xs font-medium text-status-warn">
          {t('signoff.orphaned')}
        </span>
      ) : null}

      {drifted ? (
        <span className="rounded-btn bg-status-warn/10 px-2 py-1 text-xs font-medium text-status-warn">
          {t('signoff.evidenceDrift')}
        </span>
      ) : null}

      {/* #88: wer zuletzt widerrufen hat und warum — nur an offenen Karten; Altbestand vor der
          Begründungspflicht hat keinen Grund, dafür gibt es die Kurzform. */}
      {!confirmed && revocation ? (
        <span className="text-xs text-text-muted">
          {t(revocation.reason ? 'signoff.lastRevocation' : 'signoff.lastRevocationNoReason', {
            date: revocation.revoked_at.slice(0, 10),
            name: revocation.revoked_by_name ?? '—',
            reason: revocation.reason,
          })}
        </span>
      ) : null}

      {/* #83 (Task 6): "woraus speist sich das?" — die Prozess-Herkunft steht VOR den
          Nachweiszahlen, damit die Zahlen direkt darunter erklärbar sind. */}
      <ProcessOriginDisclosure origin={item.process_origin} />

      {/* #77: die Nachweiszahlen stehen bewusst VOR dem Bestätigen-Knopf — die
          Entscheidungsgrundlage gehört vor die Entscheidung. Whole-Branch-Review: der "0 / 0"-Fall
          KANN wieder vorkommen — genau dann, wenn `orphaned` oben zuschlägt. Die Zahlen bleiben
          trotzdem stehen (0 / 0 ist Teil des Belegs, nicht etwas, das man verstecken müsste). */}
      {item.evidence ? (
        <span className="text-xs text-text-body">
          {t('signoff.evidence', {
            passed: item.evidence.passed_run_count,
            total: item.evidence.total_in_scope,
          })}
        </span>
      ) : null}

      {canConfirm && !confirmed ? (
        <button
          type="button"
          onClick={onConfirm}
          disabled={pending}
          className="self-start rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
        >
          {confirmLabel(t, signoffTerm)}
        </button>
      ) : null}

      {canConfirm && confirmed ? (
        <button
          type="button"
          onClick={onRevoke}
          disabled={pending}
          className="self-start rounded-btn border border-[var(--border)] px-3 py-1.5 text-sm text-text-body hover:text-status-fail disabled:opacity-60"
        >
          {t('signoff.revoke')}
        </button>
      ) : null}
    </div>
  );
}

export function SignoffPage() {
  const { t, i18n } = useTranslation();
  const { user } = useScopedUser();
  const queryClient = useQueryClient();
  const { project, isLoading: projectLoading } = useCurrentProject();
  const projectId = project?.project_id;
  const [searchParams] = useSearchParams();

  // #55: der Sign-off hängt jetzt an einem konkreten Testplan statt implizit am "aktiven" Plan
  // geraten zu werden. Ersetzt den Notbehelf aus Task 2 (defaultActivePlanId ohne UI, ohne
  // Wechselmöglichkeit, ohne sichtbaren Hinweis worüber abgenommen wird) durch einen echten,
  // sichtbaren Selektor — Muster aus reporting-page.tsx:76-90.
  const plansQuery = useQuery({
    queryKey: ['plans', projectId],
    queryFn: () => plansApi.list(projectId!),
    enabled: Boolean(projectId),
  });
  const activePlans = sortActivePlans(plansQuery.data ?? []);
  const activePlanIdsKey = activePlans.map((p) => p.test_plan_id).join(',');

  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  // #96: Rückfrage vor Bestätigen UND Widerrufen — nennt den betroffenen Knoten beim Namen.
  // #88: die Rückfrage trägt jetzt ein Textfeld. Beim Bestätigen ist der Kommentar Pflicht, sobald
  // nicht alle Testfälle im Scope bestanden sind (`needsNote`, Kundenentscheidung 13.10.), sonst
  // freiwillig; beim Widerrufen ist die Begründung immer Pflicht. Der Server erzwingt beides
  // ebenfalls — hier nur, damit der Knopf gar nicht erst zu einem 400 führt.
  const [confirmAction, setConfirmAction] = useState<
    | { kind: 'confirm'; nodeId: string; nodeName: string; needsNote: boolean }
    | { kind: 'revoke'; signoffId: string; nodeName: string }
    | null
  >(null);
  // Ein Entwurf für beide Dialogarten (es ist immer nur einer offen); wird bei jedem Öffnen
  // geleert, damit ein abgebrochener Text nicht im nächsten Dialog (anderer Knoten!) wieder auftaucht.
  const [draft, setDraft] = useState('');
  const openConfirmAction = (action: NonNullable<typeof confirmAction>) => {
    setDraft('');
    setConfirmAction(action);
  };

  // Vorbelegung/Neubelegung hängt an der LISTE der aktiven Plan-IDs, nicht am Array-OBJEKT
  // `activePlans` — sonst würde jeder Refetch (z. B. nach invalidate() bei Confirm/Revoke) die
  // Wahl des Nutzers stillschweigend überschreiben (dieselbe Falle wie im Testplan-Detail,
  // siehe plan-detail.tsx:66-78 — dort bereits einmal genau so aufgetreten und gefixt). Ein
  // gültiger `?test_plan_id`-Deeplink (Task 4 verlinkt so aus der Testplan-Ansicht hierher)
  // gewinnt über die Default-Regel aus plan-selection.ts; ein unbekannter oder inaktiver Wert
  // wird ignoriert statt als Fehler behandelt.
  // Branch-Review W1: die Auswahl wurde hier BEDINGUNGSLOS neu gesetzt, sobald sich
  // `activePlanIdsKey` änderte (z. B. weil irgendwer im Projekt einen weiteren Plan aktiviert
  // hat) — eine bereits gültige Hand-Auswahl des Nutzers sprang dabei lautlos auf den Default
  // zurück, und die nächste Abnahme landete am FALSCHEN Plan (test_plan_id ist Teil des
  // Abnahme-Datensatzes). Gleiches funktionales Muster wie reporting-page.tsx/my-tests-page.tsx:
  // eine noch gültige Auswahl bleibt zuerst erhalten; nur wenn sie nicht mehr existiert, greift
  // Deeplink bzw. Default. Ein in der URL stehenbleibender `?test_plan_id`-Deeplink darf dabei
  // eine zwischenzeitliche Handauswahl nicht erneut überstimmen.
  // biome-ignore lint/correctness/useExhaustiveDependencies: bewusst nur die ID-Liste, s. o.
  useEffect(() => {
    setSelectedPlanId((cur) => {
      if (cur && activePlans.some((p) => p.test_plan_id === cur)) return cur;
      const deeplink = searchParams.get('test_plan_id');
      return activePlans.some((p) => p.test_plan_id === deeplink)
        ? deeplink
        : (activePlans[0]?.test_plan_id ?? null);
    });
  }, [activePlanIdsKey]);

  const signoffsQuery = useQuery({
    queryKey: ['signoffs', projectId, selectedPlanId],
    queryFn: () => signoffApi.list(projectId!, selectedPlanId!),
    enabled: Boolean(projectId && selectedPlanId),
  });
  const { data, isLoading, isError: signoffsError } = signoffsQuery;
  // Whole-Branch-Review (Umbau von #83): `data` ist wieder die VOLLE, ungefilterte Liste — Server
  // liefert Rohdaten, der Client entscheidet über isVisibleSignoffCard, was eine Karte wird. Ein
  // Knoten ohne Testfall im Scope UND ohne aktiven Sign-off landet stattdessen in `emptyNodes`
  // (aufklappbare Zähler-Zeile, EmptyNodesDisclosure) — ein Knoten MIT aktivem Sign-off ist IMMER
  // in `items`, auch wenn seine Grundlage weg ist (siehe isOrphaned in SignoffCard).
  const rawItems = data ?? [];
  const items = rawItems.filter(isVisibleSignoffCard);
  const emptyNodes = rawItems.filter((it) => !isVisibleSignoffCard(it));

  // Task 7: Zuschnitt-Hinweis. `useScopedUser()` liefert den bereits auf das Projekt gescopten
  // Nutzer (#23) — Knotenliste ist `rawItems` (die volle, ungefilterte Sign-off-Liste dieses
  // Plans trägt node_id/node_name schon); eine zweite Abfrage wäre unnötig.
  const nodesForHint = rawItems.map((it) => ({ node_id: it.node_id, name: it.node_name }));
  const scopeHint = scopeHintFrom(user, nodesForHint);

  // #34: „Ohne Zuordnung"-Sicht — In-Scope-Testfälle, die an keinem Sign-off-Knoten hängen. Hängt
  // wie die Kartenliste am gewählten Plan; ein Plan-Wechsel invalidiert die Query über den
  // queryKey, ein zusätzliches invalidate() bei Confirm/Revoke ist nicht nötig (Bestätigen/
  // Widerrufen ändern die Zuordnung nicht).
  // #108: volles Query-Objekt statt schlanker Destrukturierung — scheiterte diese Query bisher,
  // blieb `unassigned` `undefined`, die Warnbox unten erschien einfach nicht (count > 0 greift
  // nie), ununterscheidbar von "alles zugeordnet". isError + refetch tragen jetzt die kompakte
  // Fehlerzeile direkt vor der Warnbox.
  const unassignedQuery = useQuery({
    queryKey: ['signoffs-unassigned', projectId, selectedPlanId],
    queryFn: () => signoffApi.unassigned(projectId!, selectedPlanId!),
    enabled: Boolean(projectId && selectedPlanId),
  });
  const unassigned = unassignedQuery.data;

  // Solange die Einstellung noch nicht geladen ist, bleiben confirmLabel/confirmedStatusLabel
  // beim festen `signoff.confirm`/`signoff.confirmed`-Text (graceful fallback, kein Layout-Sprung).
  const settingsQuery = useQuery({
    queryKey: ['project-settings', projectId],
    queryFn: () => projectConfigApi.getSettings(projectId ?? ''),
    enabled: Boolean(projectId),
  });
  const signoffTerm = settingsQuery.data?.signoff_term;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['signoffs', projectId] });
  };

  const confirmMutation = useMutation({
    mutationFn: ({ nodeId, note }: { nodeId: string; note: string }) =>
      signoffApi.confirm(projectId!, {
        node_id: nodeId,
        test_plan_id: selectedPlanId!,
        // #88: leer = weglassen (der Server trimmt ohnehin, ein Leerstring wäre kein Kommentar).
        note: note.trim() || undefined,
      }),
    meta: { successToast: 'feedback.saved' },
    onSuccess: () => {
      invalidate();
      setConfirmAction(null);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: ({ signoffId, reason }: { signoffId: string; reason: string }) =>
      signoffApi.revoke(projectId!, signoffId, reason.trim()),
    meta: { successToast: 'feedback.saved' },
    onSuccess: () => {
      invalidate();
      setConfirmAction(null);
    },
  });

  if (projectLoading) return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  if (!project) return <div className="p-6 text-text-muted">{t('signoff.noProject')}</div>;
  // Kein stiller Rückfall: ohne aktiven Plan gibt es keine Kartenliste und keine Abfrage —
  // stattdessen ein sichtbarer Hinweis. #89: schlägt plansQuery selbst fehl, bleibt `activePlans`
  // ebenfalls leer (`plansQuery.data` ist `undefined`) — ohne den zusätzlichen `!isError`-Schutz
  // würde ein Netzfehler hier fälschlich als "gar kein aktiver Testplan" gemeldet (Kernsatz:
  // Query-Fehler darf NIE als Leerzustand erscheinen).
  if (!plansQuery.isLoading && !plansQuery.isError && activePlans.length === 0) {
    return <div className="p-6 text-text-muted">{t('signoff.noActivePlan')}</div>;
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-text-primary">{t('signoff.title')}</h1>

        {/* Sichtbarer Selektor, sobald mindestens ein Plan aktiv ist (auch bei genau einem) —
            auf der Sign-off-Seite muss immer erkennbar sein, worüber gerade abgenommen wird,
            plus die Möglichkeit, den Plan zu wechseln. */}
        {activePlans.length > 0 ? (
          <label className="flex items-center gap-2 text-sm">
            <span className="text-text-muted">{t('signoff.plan')}</span>
            <select
              className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1"
              value={selectedPlanId ?? ''}
              onChange={(e) => setSelectedPlanId(e.target.value)}
            >
              {activePlans.map((p) => (
                <option key={p.test_plan_id} value={p.test_plan_id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {/* Task 7: derselbe Zuschnitt, der Task 4-6 die Backend-Zahlen filtern lässt — ohne diesen
          Hinweis liest ein Verantwortlicher für "Vertrieb" seine Karten als vollständige
          Projektsicht (feature-durchstich, "Vollständigkeit"-Regel des Projekts).
          Fund 1 (Review): erscheint asynchron und ist die einzige Warnung, dass die Zahlen
          beschnitten sind — aria-live="polite" statt eines stillen DOM-Updates. Ohne aufgelöste
          Knotennamen (scopeHint.label === null) greift der namenlose Rückfalltext statt gar
          keinem Hinweis. */}
      {scopeHint ? (
        <p className="mb-3 text-sm text-text-muted" data-testid="scope-hint" aria-live="polite">
          {scopeHint.label !== null
            ? t('scope.hint', { nodes: scopeHint.label })
            : t('scope.hintFallback')}
        </p>
      ) : null}

      {isLoading || plansQuery.isLoading ? (
        <div className="text-sm text-text-muted">{t('common.loading')}</div>
      ) : null}
      {/* Fix nach Task-8-Review: schlägt plansQuery selbst fehl, greift oben nur der `!isError`-
          Schutz vor der irreführenden `noActivePlan`-Meldung — der Plan-Selektor bleibt zu Recht
          weg (activePlans.length === 0), aber ohne diese Karte stand darunter nichts mehr: ein
          stiller Blank-Zustand, genau die Fehlerklasse, die #89 verhindern soll. */}
      {plansQuery.isError ? <QueryErrorNotice onRetry={() => plansQuery.refetch()} /> : null}
      {/* #89: `data` bleibt bei einer gescheiterten signoffs-Query `undefined` — `items` ist dann
          genauso leer wie bei "wirklich keine Knoten", und der Text unten würde einen Netzfehler
          fälschlich als Leerzustand feiern (Kernsatz). Karte statt Text; Retry lädt gezielt neu. */}
      {signoffsError ? <QueryErrorNotice onRetry={() => signoffsQuery.refetch()} /> : null}
      {/* #83 (Nutzerentscheidung, verbindlich): Karten mit total_in_scope === 0 UND ohne aktiven
          Sign-off sind ausgeblendet (isVisibleSignoffCard). Ein leeres `items` kann deshalb zwei
          fachlich verschiedene Dinge bedeuten — das darf sich NICHT beide Male wie "kein Sign-off
          nötig" lesen:
          - GAR KEINE sign-off-fähigen Knoten im Projekt (rawItems leer) -> die alte, generische
            Meldung.
          - Knoten existieren, aber KEINER hat einen Testfall in DIESEM Plan (rawItems nicht leer,
            aber alle in emptyNodes gelandet) -> ein eigener Hinweis, plus die aufklappbare
            Zähler-Zeile bleibt sichtbar (sonst sähe die Seite aus, als gäbe es nichts zu tun,
            obwohl in Wahrheit nur dieser Plan noch nichts abdeckt).
          #89: zusätzlich vor signoffsError/plansQuery.isError geschützt — sonst würde bei einem
          der beiden Fehler DIESER Text zusätzlich zur (oder statt der) Fehlerkarte erscheinen. */}
      {!isLoading &&
      !plansQuery.isLoading &&
      !signoffsError &&
      !plansQuery.isError &&
      items.length === 0 ? (
        <div className="text-sm text-text-muted">
          {t(emptyNodes.length > 0 ? 'signoff.allEmptyInPlan' : 'signoff.noNodes')}
        </div>
      ) : null}

      {unassignedQuery.isError ? (
        <QueryErrorNotice compact onRetry={() => unassignedQuery.refetch()} className="mb-2" />
      ) : null}

      {unassigned && unassigned.count > 0 ? (
        <div className="flex flex-col gap-2 rounded-card border border-status-warn/40 bg-status-warn/5 p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-bold text-text-primary">{t('signoff.unassignedTitle')}</h2>
            <StateChip
              label={t('signoff.unassignedEvidence', {
                passed: unassigned.passed_count,
                total: unassigned.count,
              })}
              tone="warn"
            />
          </div>
          <span className="text-sm text-text-muted">{t('signoff.unassignedDesc')}</span>
          <ul className="flex flex-col gap-1">
            {unassigned.test_cases.map((tc) => (
              <li key={tc.test_case_id} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-text-body">
                  <span className="font-medium text-text-primary">{tc.human_id}</span>{' '}
                  {pickLang(i18n.resolvedLanguage, tc.title_de, tc.title_en)}
                </span>
                {/* #34: echtes Ergebnis über den zentralen ResultChip (RESULT_TONE) — ein
                    fehlgeschlagener Testfall zeigt „Fehler", nicht „Offen"; noch nie ausgeführt =
                    'open'. Keine eigene Status→Ton-Zuordnung, die von den anderen Sichten driftet. */}
                <ResultChip status={tc.latest_result ?? 'open'} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <SignoffCard
            key={item.node_id}
            item={item}
            canConfirm={user ? can(user, 'signoff.confirm', { node_id: item.node_id }) : false}
            pending={confirmMutation.isPending || revokeMutation.isPending}
            signoffTerm={signoffTerm}
            onConfirm={() =>
              openConfirmAction({
                kind: 'confirm',
                nodeId: item.node_id,
                nodeName: item.node_name,
                needsNote: needsSignoffNote(item),
              })
            }
            onRevoke={() => {
              if (item.signoff_id) {
                openConfirmAction({
                  kind: 'revoke',
                  signoffId: item.signoff_id,
                  nodeName: item.node_name,
                });
              }
            }}
          />
        ))}
      </div>

      {/* #83: die ausgeblendeten Knoten — keine stille Kappung, aufklappbar mit Namen. Erscheint
          unabhängig davon, ob oben schon Karten oder der allEmptyInPlan-Hinweis stehen. */}
      <EmptyNodesDisclosure nodes={emptyNodes} />

      {confirmAction?.kind === 'confirm' && (
        <ConfirmDialog
          title={confirmLabel(t, signoffTerm)}
          message={t('signoff.confirmConfirm', { node: confirmAction.nodeName })}
          confirmLabel={confirmLabel(t, signoffTerm)}
          pending={confirmMutation.isPending}
          confirmDisabled={confirmAction.needsNote && draft.trim() === ''}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => confirmMutation.mutate({ nodeId: confirmAction.nodeId, note: draft })}
        >
          <DraftField
            id="signoff-note"
            label={t('signoff.noteLabel')}
            hint={confirmAction.needsNote ? t('signoff.noteRequired') : null}
            value={draft}
            onChange={setDraft}
          />
        </ConfirmDialog>
      )}
      {confirmAction?.kind === 'revoke' && (
        <ConfirmDialog
          title={t('signoff.revoke')}
          message={t('signoff.confirmRevoke', { node: confirmAction.nodeName })}
          confirmLabel={t('signoff.revoke')}
          danger
          pending={revokeMutation.isPending}
          confirmDisabled={draft.trim() === ''}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() =>
            revokeMutation.mutate({ signoffId: confirmAction.signoffId, reason: draft })
          }
        >
          <DraftField
            id="signoff-revoke-reason"
            label={t('signoff.revokeReasonLabel')}
            // #88 (Final-Review F8): sonst bliebe unklar, warum „Widerrufen“ gesperrt ist.
            hint={draft.trim() === '' ? t('signoff.revokeReasonRequired') : null}
            value={draft}
            onChange={setDraft}
          />
        </ConfirmDialog>
      )}
    </div>
  );
}
